import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Loader2, LogOut, UserCog, QrCode } from "lucide-react";
import { QrScanner } from "@/components/QrScanner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function CheckOutPage() {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [accent, setAccent] = useState("");
  useEffect(() => { api.get("/settings/badge").then(({ data }) => setAccent(data.accent_color || data.brand_color || "")).catch(() => {}); }, []);

  const onScan = useCallback(async (code) => {
    const m = /^LGRD-VISIT:([\w-]+)$/.exec((code || "").trim());
    if (!m) return { label: "Not a Logerd badge", sub: "Try again", tone: "amber" };
    try {
      const { data } = await api.post("/visits/checkout", { visit_id: m[1] });
      nav("/kiosk/success", { state: { mode: "out", name: data.full_name, time: data.checked_out_at, duration: data.duration_minutes } });
      return { label: `Goodbye, ${data.full_name}`, sub: "Signed out" };
    } catch (err) {
      return { label: formatApiErrorDetail(err.response?.data?.detail) || "Could not sign out", sub: "Ask staff for help", tone: "amber" };
    }
  }, [nav]);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    setHasSearched(true);
    try {
      const { data } = await api.get(`/visits/active-lookup`, { params: { q: q.trim() } });
      setResults(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const checkout = async (id, name) => {
    setSubmittingId(id);
    try {
      const { data } = await api.post("/visits/checkout", { visit_id: id });
      nav("/kiosk/success", {
        state: { mode: "out", name: data.full_name, time: data.checked_out_at, duration: data.duration_minutes },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Could not check out ${name}.`);
      setSubmittingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      {scanning && <QrScanner title="Scan your badge" hint="Hold the QR code on your visitor badge inside the frame." onScan={onScan} onClose={() => setScanning(false)} color={accent || "hsl(var(--primary))"} />}
      <div className="mx-auto max-w-3xl px-6 py-8 md:py-12">
        <Link to="/kiosk" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6" data-testid="back-to-kiosk">
          <ArrowLeft className="h-4 w-4" /> <span className="text-sm uppercase tracking-widest">Back</span>
        </Link>

        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Check Out</h1>
        <p className="text-lg text-slate-600 mt-2">Find your check-in by name, phone, or email.</p>

        {user && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs" data-testid="checkout-on-duty">
            <UserCog className="h-3.5 w-3.5 text-emerald-700" />
            <span className="text-emerald-800">Handled by <span className="font-semibold">{user.name}</span></span>
          </div>
        )}

        <button
          type="button"
          onClick={() => setScanning(true)}
          data-testid="checkout-scan-badge-btn"
          className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-2xl border-2 border-primary bg-white py-5 text-primary font-display text-xl font-semibold tracking-tight transition active:scale-[0.98] hover:bg-primary/5"
        >
          <QrCode className="h-6 w-6" /> Scan your badge to sign out
        </button>
        <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400"><span className="h-px flex-1 bg-slate-200" /> or search <span className="h-px flex-1 bg-slate-200" /></div>

        <form onSubmit={search} className="mt-4 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="checkout-search-input"
              placeholder="Jane Smith or 555-1234"
              className="w-full h-16 rounded-xl border-2 border-slate-200 bg-white pl-14 pr-5 text-lg md:text-xl font-body text-slate-900 placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition"
            />
          </div>
          <button
            type="submit"
            disabled={searching || !q.trim()}
            data-testid="checkout-search-btn"
            className="px-8 rounded-xl bg-slate-900 text-white font-display text-lg font-semibold tracking-tight transition active:scale-[0.98] disabled:opacity-60 hover:bg-slate-800"
          >
            {searching ? <Loader2 className="h-5 w-5 animate-spin" /> : "Search"}
          </button>
        </form>

        <div className="mt-8 space-y-3" data-testid="checkout-results">
          {hasSearched && !searching && results.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-10 text-center text-slate-500">
              No active check-ins found for “{q}”. Please ask staff for help.
            </div>
          )}

          {results.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4"
              data-testid={`checkout-row-${v.id}`}
            >
              <div>
                <div className="font-display text-xl font-semibold text-slate-900">{v.full_name}</div>
                <div className="text-sm text-slate-600">
                  {v.company ? `${v.company} · ` : ""}Visiting <span className="font-medium text-slate-800">{v.host_name}</span>
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">
                  Checked in {new Date(v.checked_in_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => checkout(v.id, v.full_name)}
                disabled={submittingId === v.id}
                data-testid={`confirm-checkout-${v.id}`}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-white font-display text-base font-semibold tracking-tight transition active:scale-[0.98] disabled:opacity-60 hover:shadow"
              >
                {submittingId === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                Sign Out
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
